import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { Dialog } from 'primeng/dialog';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import { TableModule } from 'primeng/table';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'primeng/tabs';
import { Textarea } from 'primeng/textarea';
import { Toast } from 'primeng/toast';
import { Tooltip } from 'primeng/tooltip';
import { FakeReviewService, UpdateAuthorPatch } from '@/app/services/fake-review.service';
import { FakeReviewAuthor, FakeReviewAuthorAddDto } from '@/lib-backend';

interface AuthorRow {
    id: number;
    Username: string;
    AvatarUrl: string;
}

interface EditForm {
    Username: string;
    AvatarUrl: string;
    IsActive: boolean;
}

const IMPORT_EXAMPLE = `[
  { "Username": "Alice", "AvatarUrl": "https://example.com/a.png" },
  { "Username": "Bob",   "AvatarUrl": null }
]`;

const MANAGE_FETCH_CHUNK = 500;

type TabKey = 'add' | 'manage';

@Component({
    selector: 'app-fake-authors',
    standalone: true,
    imports: [
        FormsModule,
        Button,
        Checkbox,
        Dialog,
        IconField,
        InputIcon,
        InputText,
        Message,
        TableModule,
        Tab,
        TabList,
        TabPanel,
        TabPanels,
        Tabs,
        Textarea,
        Toast,
        Tooltip,
    ],
    providers: [MessageService],
    templateUrl: './fake-authors.html',
    styleUrl: './fake-authors.scss',
})
export class FakeAuthors {
    private readonly fakeReviewService = inject(FakeReviewService);
    private readonly messageService = inject(MessageService);

    private nextRowId = 1;

    // ── Tabs ────────────────────────────────────────────────────────────────
    protected readonly activeTab = signal<TabKey>('add');

    // ── Add tab ─────────────────────────────────────────────────────────────
    protected readonly rows = signal<AuthorRow[]>([this.makeRow()]);
    protected readonly saving = signal(false);
    protected readonly error = signal<string | null>(null);

    protected readonly pageSize = signal(20);
    protected readonly firstIndex = signal(0);
    protected readonly pageSizeOptions = [10, 20, 50, 100];

    protected readonly validCount = computed(
        () => this.rows().filter((r) => r.Username.trim().length > 0).length,
    );

    // ── Confirmation dialogs (Add tab) ──────────────────────────────────────
    protected readonly clearConfirmVisible = signal(false);
    protected readonly removeConfirmVisible = signal(false);
    protected readonly removeConfirmRow = signal<AuthorRow | null>(null);
    protected readonly removeConfirmDontAsk = signal(false);
    /** Session-wide: once user opts out, remove-row prompts are skipped. */
    private skipRemoveConfirm = false;

    // ── Import dialog (Add tab) ─────────────────────────────────────────────
    protected readonly importDialogVisible = signal(false);
    protected readonly importJsonText = signal('');
    protected readonly importError = signal<string | null>(null);
    protected readonly importDragOver = signal(false);
    protected readonly importExample = IMPORT_EXAMPLE;

    // ── Manage tab ──────────────────────────────────────────────────────────
    protected readonly allAuthors = signal<FakeReviewAuthor[]>([]);
    protected readonly manageLoading = signal(false);
    protected readonly manageError = signal<string | null>(null);
    protected readonly manageActiveOnly = signal(false);
    protected readonly manageSearch = signal('');
    protected readonly togglingId = signal<string | null>(null);
    private manageInitialized = false;

    protected readonly filteredAuthors = computed(() => {
        const list = this.allAuthors();
        const activeOnly = this.manageActiveOnly();
        const search = this.manageSearch().trim().toLowerCase();
        return list.filter((a) => {
            if (activeOnly && !a.IsActive) return false;
            if (search && !a.Username.toLowerCase().includes(search)) {
                return false;
            }
            return true;
        });
    });

    // ── Edit dialog (Manage tab) ────────────────────────────────────────────
    protected readonly editDialogVisible = signal(false);
    protected readonly editAuthor = signal<FakeReviewAuthor | null>(null);
    protected readonly editForm = signal<EditForm | null>(null);
    protected readonly editSaving = signal(false);
    protected readonly editError = signal<string | null>(null);

    // ────────────────────────────────────────────────────────────────────────
    // Add tab
    // ────────────────────────────────────────────────────────────────────────

    protected addRow(): void {
        this.rows.update((list) => [...list, this.makeRow()]);
        this.jumpToLastPage();
    }

    protected requestRemoveRow(row: AuthorRow): void {
        if (this.isRowBlank(row) || this.skipRemoveConfirm) {
            this.removeRow(row);
            return;
        }
        this.removeConfirmRow.set(row);
        this.removeConfirmDontAsk.set(false);
        this.removeConfirmVisible.set(true);
    }

    protected confirmRemoveRow(): void {
        const row = this.removeConfirmRow();
        if (this.removeConfirmDontAsk()) this.skipRemoveConfirm = true;
        this.removeConfirmVisible.set(false);
        this.removeConfirmRow.set(null);
        if (row) this.removeRow(row);
    }

    protected cancelRemoveRow(): void {
        this.removeConfirmVisible.set(false);
        this.removeConfirmRow.set(null);
    }

    private removeRow(row: AuthorRow): void {
        this.rows.update((list) => list.filter((r) => r.id !== row.id));
        if (this.rows().length === 0) this.addRow();
        this.clampFirstIndex();
    }

    protected onPage(event: { first: number; rows: number }): void {
        this.firstIndex.set(event.first);
        this.pageSize.set(event.rows);
    }

    /** Set first index to the start of the page that contains the last row. */
    private jumpToLastPage(): void {
        const total = this.rows().length;
        const size = this.pageSize();
        this.firstIndex.set(Math.max(0, Math.floor((total - 1) / size) * size));
    }

    /** Keep firstIndex within bounds after removal. */
    private clampFirstIndex(): void {
        const total = this.rows().length;
        const size = this.pageSize();
        const maxFirst = Math.max(0, Math.floor((total - 1) / size) * size);
        if (this.firstIndex() > maxFirst) this.firstIndex.set(maxFirst);
    }

    protected async save(): Promise<void> {
        this.error.set(null);

        const rows = this.rows();
        const payload: FakeReviewAuthorAddDto[] = [];
        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            const username = r.Username.trim();
            if (!username) continue;

            const avatar = r.AvatarUrl.trim();
            payload.push({
                Username: username,
                AvatarUrl: avatar.length > 0 ? avatar : null,
            });
        }

        if (payload.length === 0) {
            this.error.set('Add at least one row with a non-empty Username.');
            return;
        }

        this.saving.set(true);
        try {
            const outcome = await this.fakeReviewService.addAuthors(payload);
            if (outcome.result === 'Ok') {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Authors saved',
                    detail: `${outcome.insertedCount} of ${payload.length} inserted.`,
                });
                this.reset();
                if (this.manageInitialized) this.reloadAuthors();
            } else {
                this.error.set('Failed to save authors.');
            }
        } finally {
            this.saving.set(false);
        }
    }

    protected requestClear(): void {
        const allBlank = this.rows().every((r) => this.isRowBlank(r));
        if (allBlank) {
            this.reset();
            return;
        }
        this.clearConfirmVisible.set(true);
    }

    protected confirmClear(): void {
        this.clearConfirmVisible.set(false);
        this.reset();
    }

    protected cancelClear(): void {
        this.clearConfirmVisible.set(false);
    }

    private reset(): void {
        this.rows.set([this.makeRow()]);
        this.error.set(null);
        this.firstIndex.set(0);
    }

    private makeRow(): AuthorRow {
        return { id: this.nextRowId++, Username: '', AvatarUrl: '' };
    }

    private isRowBlank(r: AuthorRow): boolean {
        return (
            r.Username.trim().length === 0 && r.AvatarUrl.trim().length === 0
        );
    }

    protected onAvatarError(event: Event): void {
        const img = event.target as HTMLImageElement;
        img.style.display = 'none';
    }

    // ── Import dialog ───────────────────────────────────────────────────────

    protected openImport(): void {
        this.importJsonText.set('');
        this.importError.set(null);
        this.importDragOver.set(false);
        this.importDialogVisible.set(true);
    }

    protected closeImport(): void {
        this.importDialogVisible.set(false);
    }

    protected onImportFileChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        input.value = '';
        if (file) this.readFile(file);
    }

    protected onImportDragOver(event: DragEvent): void {
        event.preventDefault();
        this.importDragOver.set(true);
    }

    protected onImportDragLeave(): void {
        this.importDragOver.set(false);
    }

    protected onImportDrop(event: DragEvent): void {
        event.preventDefault();
        this.importDragOver.set(false);
        const file = event.dataTransfer?.files?.[0];
        if (file) this.readFile(file);
    }

    private readFile(file: File): void {
        this.importError.set(null);
        const reader = new FileReader();
        reader.onload = () => {
            this.importJsonText.set(
                typeof reader.result === 'string' ? reader.result : '',
            );
        };
        reader.onerror = () => {
            this.importError.set('Failed to read file.');
        };
        reader.readAsText(file);
    }

    protected confirmImport(): void {
        const raw = this.importJsonText().trim();
        if (!raw) {
            this.importError.set('Paste JSON or drop a file.');
            return;
        }

        let parsed: unknown;
        try {
            parsed = JSON.parse(raw);
        } catch (e) {
            this.importError.set(`Invalid JSON: ${(e as Error).message}`);
            return;
        }

        if (!Array.isArray(parsed)) {
            this.importError.set('Top-level value must be a JSON array.');
            return;
        }

        const result = this.parseImport(parsed);
        if (result.error) {
            this.importError.set(result.error);
            return;
        }

        this.rows.update((list) => {
            const trimmed = list.filter((r) => !this.isRowBlank(r));
            return [...trimmed, ...result.rows];
        });
        this.jumpToLastPage();
        this.messageService.add({
            severity: 'success',
            summary: 'Authors imported',
            detail: `${result.rows.length} row(s) added — review and click Save.`,
        });
        this.importDialogVisible.set(false);
    }

    private parseImport(arr: unknown[]): {
        rows: AuthorRow[];
        error: string | null;
    } {
        const rows: AuthorRow[] = [];
        for (let i = 0; i < arr.length; i++) {
            const item = arr[i];
            if (item === null || typeof item !== 'object') {
                return { rows: [], error: `Item ${i}: must be an object.` };
            }
            const obj = item as Record<string, unknown>;

            const username = obj['Username'];
            if (typeof username !== 'string' || username.trim().length === 0) {
                return {
                    rows: [],
                    error: `Item ${i}: "Username" is required and must be a non-empty string.`,
                };
            }

            const avatarRaw = obj['AvatarUrl'];
            let avatar = '';
            if (avatarRaw !== null && avatarRaw !== undefined) {
                if (typeof avatarRaw !== 'string') {
                    return {
                        rows: [],
                        error: `Item ${i}: "AvatarUrl" must be a string or null.`,
                    };
                }
                avatar = avatarRaw.trim();
            }

            rows.push({
                id: this.nextRowId++,
                Username: username.trim(),
                AvatarUrl: avatar,
            });
        }
        return { rows, error: null };
    }

    protected trackByRowId(_index: number, item: AuthorRow): number {
        return item.id;
    }

    // ────────────────────────────────────────────────────────────────────────
    // Manage tab
    // ────────────────────────────────────────────────────────────────────────

    protected onTabChange(value: string | number | undefined): void {
        const key = value === 'manage' ? 'manage' : 'add';
        this.activeTab.set(key);
        if (key === 'manage' && !this.manageInitialized) {
            this.manageInitialized = true;
            this.reloadAuthors();
        }
    }

    protected async reloadAuthors(): Promise<void> {
        if (this.manageLoading()) return;
        this.manageLoading.set(true);
        this.manageError.set(null);
        const collected: FakeReviewAuthor[] = [];
        try {
            // Always pull the full set (ActiveOnly=false) and filter client-side
            // so the "Active only" toggle and search react instantly.
            let skip = 0;
            // eslint-disable-next-line no-constant-condition
            while (true) {
                const outcome = await this.fakeReviewService.listAuthors(
                    false,
                    skip,
                    MANAGE_FETCH_CHUNK,
                );
                if (outcome.result !== 'Ok') {
                    this.manageError.set('Failed to load authors.');
                    return;
                }
                collected.push(...outcome.authors);
                if (outcome.authors.length < MANAGE_FETCH_CHUNK) break;
                skip += MANAGE_FETCH_CHUNK;
            }
            this.allAuthors.set(collected);
        } finally {
            this.manageLoading.set(false);
        }
    }

    protected async toggleActive(author: FakeReviewAuthor, newValue: boolean): Promise<void> {
        if (this.togglingId() !== null) return;
        this.togglingId.set(author.Id);
        try {
            const outcome = await this.fakeReviewService.updateAuthor(author.Id, {
                Username: null,
                AvatarUrl: null,
                IsActive: newValue,
            });
            if (outcome.result === 'Ok') {
                this.applyAuthorUpdate(outcome.author);
                this.messageService.add({
                    severity: 'success',
                    summary: newValue ? 'Author activated' : 'Author deactivated',
                    detail: outcome.author.Username,
                });
            } else if (outcome.result === 'NotFound') {
                this.messageService.add({
                    severity: 'warn',
                    summary: 'Author not found',
                    detail: 'It may have been removed elsewhere.',
                });
                this.removeAuthorLocal(author.Id);
            }
        } finally {
            this.togglingId.set(null);
        }
    }

    protected openEdit(author: FakeReviewAuthor): void {
        this.editAuthor.set(author);
        this.editForm.set({
            Username: author.Username,
            AvatarUrl: author.AvatarUrl ?? '',
            IsActive: author.IsActive,
        });
        this.editError.set(null);
        this.editDialogVisible.set(true);
    }

    protected closeEdit(): void {
        this.editDialogVisible.set(false);
        this.editAuthor.set(null);
        this.editForm.set(null);
        this.editError.set(null);
    }

    protected updateEditUsername(value: string): void {
        this.editForm.update((f) => (f ? { ...f, Username: value } : f));
    }

    protected updateEditAvatar(value: string): void {
        this.editForm.update((f) => (f ? { ...f, AvatarUrl: value } : f));
    }

    protected updateEditActive(value: boolean): void {
        this.editForm.update((f) => (f ? { ...f, IsActive: value } : f));
    }

    protected async saveEdit(): Promise<void> {
        const orig = this.editAuthor();
        const form = this.editForm();
        if (!orig || !form) return;

        const trimmedUsername = form.Username.trim();
        if (trimmedUsername.length === 0) {
            this.editError.set("Username can't be empty.");
            return;
        }

        const patch = this.computeAuthorPatch(orig, form);
        if (patch.Username === null && patch.AvatarUrl === null && patch.IsActive === null) {
            this.editError.set('No changes.');
            return;
        }

        this.editSaving.set(true);
        this.editError.set(null);
        try {
            const outcome = await this.fakeReviewService.updateAuthor(orig.Id, patch);
            if (outcome.result === 'Ok') {
                this.applyAuthorUpdate(outcome.author);
                this.messageService.add({
                    severity: 'success',
                    summary: 'Author updated',
                    detail: outcome.author.Username,
                });
                this.closeEdit();
            } else if (outcome.result === 'NotFound') {
                this.editError.set('Author not found — it may have been removed.');
            } else {
                this.editError.set('Failed to update author.');
            }
        } finally {
            this.editSaving.set(false);
        }
    }

    private computeAuthorPatch(orig: FakeReviewAuthor, form: EditForm): UpdateAuthorPatch {
        const trimmedUsername = form.Username.trim();
        const trimmedAvatar = form.AvatarUrl.trim();
        const origAvatar = orig.AvatarUrl ?? '';

        return {
            Username: trimmedUsername !== orig.Username ? trimmedUsername : null,
            AvatarUrl:
                trimmedAvatar === origAvatar
                    ? null
                    : { value: trimmedAvatar.length > 0 ? trimmedAvatar : null },
            IsActive: form.IsActive !== orig.IsActive ? form.IsActive : null,
        };
    }

    private applyAuthorUpdate(updated: FakeReviewAuthor): void {
        this.allAuthors.update((list) =>
            list.map((a) => (a.Id === updated.Id ? updated : a)),
        );
    }

    private removeAuthorLocal(id: string): void {
        this.allAuthors.update((list) => list.filter((a) => a.Id !== id));
    }
}
